import { useStorage } from '@vueuse/core'
import { useCore } from '@/composables/core'
import { useEppo } from '@/composables/eppo'
import { useModal } from '@/composables/modal'
import { useLogger } from '@booksy-com/vue-tools'

import { FEATURE_FLAGS } from '@/lib/consts.ts'

import {
  getNextClientFromList as _getNextClientFromList,
  removeNextClientFromList as _removeNextClientFromList,
  addClientToList as _addClientToList,
  setupPinCode as _setupPinCode,
  validatePinCode as _validatePinCode,
} from './api/actions'

import type { CheckInClient, CheckInModePinCode } from './api/types.ts'

import PutPinModal from '@/components/turn-tracker/check-in-mode/put-pin-modal.vue'

function normalizeBusinessId (businessId) {
  const raw = String(businessId ?? 'unknown')
  return raw.replace(/[^a-zA-Z0-9_-]/g, '_')
}

type CallWithErrorLog = {
  <TArg extends object, TResult>(
    fn: (args: TArg & { businessId: number }) => Promise<TResult>,
    options: { rethrow: true }
  ): (args: TArg) => Promise<TResult>;
  <TArg extends object, TResult>(
    fn: (args: TArg & { businessId: number }) => Promise<TResult>,
    options?: { rethrow?: false }
  ): (args: TArg) => Promise<TResult | void>;
}

export const useTurnTrackerCheckInMode = () => {
  const logger = useLogger('tt-check-in-mode')
  const { router, store } = useCore()
  const state = store.state as {
    business: { business: { id: number } };
    app: { country: string };
  }
  const businessId = state.business.business.id
  const country = state.app.country

  const business = normalizeBusinessId(`${country}-${businessId}`)

  // TODO: temporary solution to make sure that all data saved in localStorage from the previous implementation has been removed.
  // This will be removed in the next iteration.
  try {
    localStorage.removeItem(`tt-client-queue:daily:biz:${business}`)
  } catch (e) { /* noop */ }

  const isCheckInModeFeatureAvailable = useEppo().ff(FEATURE_FLAGS.Feature_TurnTrackerCheckInMode)

  const callWithErrorLog: CallWithErrorLog = <TArg extends object, TResult> (
    fn: (args: TArg & { businessId: number }) => Promise<TResult>,
    options?: { rethrow?: boolean }
  ) => {
    return async (args: TArg) => {
      try {
        return await fn({ businessId, ...args })
      } catch (e) {
        logger.error(e)
        if (options?.rethrow) throw e
      }
    }
  }

  const addClientToList = (client: CheckInClient) => callWithErrorLog(
    _addClientToList, { rethrow: true }
  )({ businessId, payload: client })

  const getNextClientFromList = () => callWithErrorLog(
    _getNextClientFromList
  )({ businessId })

  const removeNextClientFromList = () => callWithErrorLog(
    _removeNextClientFromList
  )({ businessId })

  const setupPinCode = (pinCode: CheckInModePinCode) => callWithErrorLog(
    _setupPinCode, { rethrow: true }
  )({ businessId, payload: pinCode })

  const validatePinCode = (pinCode: CheckInModePinCode) => callWithErrorLog(
    _validatePinCode, { rethrow: true }
  )({ businessId, payload: pinCode })

  const storageKey = `tt-check-in-mode:biz:${business}`
  const isEnabled = useStorage(storageKey, false, localStorage, {
    mergeDefaults: true,
  })

  if (!isCheckInModeFeatureAvailable) {
    isEnabled.value = null
  }

  const modal = useModal()
  const disableCheckInMode = () => {
    modal.open({
      width: 300,
      content: {
        $component: {
          is: PutPinModal,
          mode: 'validate',
          '@resolve': () => {
            isEnabled.value = false
            modal.close()
            router.push({ name: 'turn-tracker.dashboard' })
          },
        },
      },
    })
  }

  const enableCheckInMode = () => {
    isEnabled.value = true
    router.push({ name: 'turn-tracker.check-in-mode' })
  }

  const setNewPinCode = () => {
    modal.open({
      width: 300,
      content: {
        $component: {
          is: PutPinModal,
          mode: 'setup',
          '@resolve': () => {
            modal.close()
          },
        },
      },
    })
  }

  return {
    addClientToList,
    getNextClientFromList,
    removeNextClientFromList,
    setupPinCode,
    validatePinCode,
    disableCheckInMode,
    enableCheckInMode,
    setNewPinCode,
    isEnabled,
    isCheckInModeFeatureAvailable,
  }
}
